/**
 * DeepLinkMatcher
 * ────────────────────────────────────────────────────────────────────
 * Listens for the `?open_matcher=1` URL parameter (set by the
 * BusinessProfilePage "Connect with X" button) and auto-opens a
 * MiniMatcher pre-loaded for that business + category.
 *
 * Mounted once at the App root. Reads URL params on every history
 * change, opens the matcher when the flag is present, and strips
 * the params off the URL when the matcher closes so a back-button
 * navigation doesn't pop the matcher up again.
 */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MiniMatcher from './MiniMatcher';

export default function DeepLinkMatcher() {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [params, setParams] = useState(null);

  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    if (qs.get('open_matcher') === '1') {
      setParams({
        category: qs.get('category') || null,
        businessId: qs.get('business_id') || null,
        businessName: qs.get('business_name') || null,
      });
      setOpen(true);
    }
  }, [location.search]);

  const handleClose = () => {
    setOpen(false);
    // Strip the deep-link params so a back-button or refresh doesn't
    // pop the matcher again. Keep any other params the user had.
    const qs = new URLSearchParams(location.search);
    qs.delete('open_matcher');
    qs.delete('category');
    qs.delete('business_id');
    qs.delete('business_name');
    const next = qs.toString();
    navigate(
      { pathname: location.pathname, search: next ? `?${next}` : '' },
      { replace: true },
    );
  };

  if (!open || !params?.category) return null;

  // sourcePlacement uses 'profile-deeplink' so HQ analytics can split
  // out leads that arrived through a profile-page click vs ones that
  // came in via the mega-menu / fab / inline triggers.
  return (
    <MiniMatcher
      open={open}
      onClose={handleClose}
      category={params.category}
      sourcePlacement="profile-deeplink"
      heading={params.businessName ? `Connect with ${params.businessName}` : 'Find your match'}
      anchorSide="right"
    />
  );
}
